import React from 'react';
import { Checkbox, Input, MessageBar, MessageBarBody, Spinner } from '@fluentui/react-components';
import { IStorefrontBundle } from '../../Api/Models/Products';
import { ProductImage } from '../Helpers/ProductImage';
import CurrencyFormat from '../Helpers/CurrencyFormat';
import { IBundleConfiguration } from './useBundleConfiguration';

interface IBundleOptionsTablesProps {
    bundle: IStorefrontBundle;
    config: IBundleConfiguration;
    canSeePricing: boolean;
    // Disables the radios/checkboxes/quantity inputs (e.g. prepared cart, where selections aren't sent).
    disabled?: boolean;
    // Optional !important style ref for the tables (merchant bundlePageConfig on the bundle page).
    includedTableRef?: React.Ref<HTMLTableElement>;
    // Suppresses the Bundle Total row (e.g. when the caller renders it separately in a sticky footer).
    hideBundleTotal?: boolean;
}

/**
 * The bundle configuration tables — grouped choices (radios), the "Additional Items" table
 * (required + optional), and the bundle total. Extracted from BundlePage so the bundle page and
 * the cart edit panel render identical layouts from one source of truth. All selection state and
 * the derived price live in `useBundleConfiguration`.
 */
const BundleOptionsTables: React.FC<IBundleOptionsTablesProps> = (props) => {
    const { bundle, config, canSeePricing, disabled, includedTableRef, hideBundleTotal } = props;
    const {
        selectedGroupProducts, setSelectedGroupProducts,
        optionalSelections, setOptionalSelections,
        itemTmpQuantities, trySetItemTmpQuantity, blurItemQuantity,
        nonGroupedProducts, estimatedPrice, isEstimating, estimateMessages,
        isSelectionLocked, itemQuantityWarnings,
    } = config;

    // A product's quantity is editable when the merchant enabled changeQuantity, mirroring the
    // overall bundle quantity input. Editing is suppressed when disabled (prepared cart).
    const renderItemQuantity = (product: { id: string; quantity: number; changeQuantity?: boolean }) => {
        if (!product.changeQuantity || disabled) return product.quantity;
        return (
            <Input
                style={{ width: '80px', textAlign: 'right' }}
                value={itemTmpQuantities[product.id] ?? product.quantity.toString()}
                onChange={(_, d) => trySetItemTmpQuantity(product.id, d.value)}
                onBlur={() => blurItemQuantity(product.id)}
            />
        );
    };

    // When the estimate can't price the current configuration (estimatedPrice === null) the server
    // returns one message per problem item (e.g. insufficient stock after a quantity change). Surface
    // them together above the tables so the shopper sees why the total isn't updating.
    const estimateWarnings = estimatedPrice === null ? Object.values(estimateMessages) : [];

    // Renders a signed adjustment (e.g. "+$5.00" / "-$2.00") using the storefront's currency format,
    // green when it lowers the price and red when it raises it. A zero adjustment reads as "Included".
    const renderAdjustment = (amount: number) => {
        if (amount === 0) return <div>Included</div>;
        return (
            <div style={{ color: amount < 0 ? 'green' : 'red' }}>
                {amount < 0 ? '-' : '+'}<CurrencyFormat value={Math.abs(amount)} />
            </div>
        );
    };

    // Shown when an item has no per-unit price available (e.g. ac-main returned no cost for it).
    const noPrice = <>—</>;

    // Group item price, computed live against the *currently selected* item in the group rather than
    // the group's default. The selected item (zero difference) shows "Included"; every other item shows
    // the +/- difference between its per-unit price and the selected item's, so swapping the pick
    // re-bases every row. Shows "—" when per-unit prices are missing.
    const renderGroupItemPrice = (
        product: IStorefrontBundle['products'][number],
        selectedProduct: IStorefrontBundle['products'][number] | undefined,
    ) => {
        if (product.pricePerUnit != null && selectedProduct?.pricePerUnit != null) {
            return renderAdjustment(product.pricePerUnit - selectedProduct.pricePerUnit);
        }
        return noPrice;
    };

    // Optional item price. Unchecked, it's an add-on the shopper would pay extra for, so it's shown as
    // a signed "+$X" in red. Once included it's part of the bundle, so it's shown as a plain price with
    // no "+" and the default (black) color. Shows "—" when the per-unit price is missing.
    const renderOptionalItemPrice = (
        product: IStorefrontBundle['products'][number],
        isSelected: boolean,
    ) => {
        if (product.pricePerUnit != null) {
            return isSelected
                ? <div><CurrencyFormat value={product.pricePerUnit} /></div>
                : renderAdjustment(product.pricePerUnit);
        }
        return noPrice;
    };

    // Required / quantity-change item price. A plain required item is always "Included". A
    // quantity-change item shows the per-unit price of each additional unit as a plain "+$X" in the
    // default color (an add-on cost, not a swap). Shows "—" when the per-unit price is missing.
    const renderRequiredItemPrice = (product: IStorefrontBundle['products'][number]) => {
        if (!product.changeQuantity) return <div>Included</div>;
        if (product.pricePerUnit != null) return <div>+<CurrencyFormat value={product.pricePerUnit} /></div>;
        return noPrice;
    };

    if (!bundle.products || bundle.products.length === 0) return null;

    const hasGroups = (bundle.groups?.length ?? 0) > 0;

    // The saved-search item is always required and pinned above groups. It renders like a standard
    // required, non-editable component (checked + disabled): the saved search's own name with the
    // resolved representative product's image (matching how saved searches show elsewhere).
    const savedSearchProduct = bundle.products.find(p => p.savedSearchId);

    return (
        <div style={{ marginTop: '1em', clear: 'both' }}>
            {/* Estimate problems (e.g. insufficient stock for an item after a quantity change) are
                surfaced above the tables so the shopper sees why the total isn't updating. */}
            {estimateWarnings.map((message, i) => (
                <MessageBar key={i} intent='warning' style={{ marginBottom: '0.75em' }}>
                    <MessageBarBody>{message}</MessageBarBody>
                </MessageBar>
            ))}
            {Object.values(itemQuantityWarnings).map((message, i) => (
                <MessageBar key={`qty-${i}`} intent='warning' style={{ marginBottom: '0.75em' }}>
                    <MessageBarBody>{message}</MessageBarBody>
                </MessageBar>
            ))}

            {/* Saved-search item: always required, pinned above groups. */}
            {savedSearchProduct && (
                <table className='bundle-table' style={{ marginBottom: '2em' }} ref={includedTableRef}>
                    <thead>
                        <tr>
                            <th style={{ width: '80px', textAlign: 'center' }}>Include</th>
                            <th style={{ width: '110px' }} />
                            <th>Product</th>
                            <th style={{ width: '100px', textAlign: 'center' }}>Quantity</th>
                            {canSeePricing && <th style={{ width: '100px', textAlign: 'right' }}>Price</th>}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                <Checkbox checked disabled />
                            </td>
                            <td>
                                <ProductImage
                                    src={savedSearchProduct.image ?? undefined}
                                    fit='contain'
                                    style={{ width: '100px', height: '100px' }}
                                />
                            </td>
                            <td>{savedSearchProduct.name}</td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>{savedSearchProduct.quantity}</td>
                            {/* The headliner is always required and never quantity-editable, so it's simply
                                "Included" — no per-unit add-on price path. */}
                            {canSeePricing && <td style={{ textAlign: 'right', verticalAlign: 'middle' }}><div>Included</div></td>}
                        </tr>
                    </tbody>
                </table>
            )}

            {/* Group sections */}
            {hasGroups && bundle.groups!.map(group => {
                // Order items by the group's own product list so they display in the configured
                // group order; the default item stays in its position rather than pulled to the top.
                const groupProducts = group.products
                    .map(pid => bundle.products.find(p => p.id === pid && p.optionality === group.id))
                    .filter((p): p is typeof bundle.products[number] => p != null);
                if (groupProducts.length === 0) return null;
                // When the rep locked the pick for this group, lock every radio in the group (not
                // just the chosen product) so the shopper can't swap the selection out from under it.
                const groupLocked = groupProducts.some(p => isSelectionLocked(p.id));
                // The currently selected item in this group — every other item's price is shown as the
                // difference from it, so the group re-bases as the shopper changes the pick.
                const selectedGroupProduct = groupProducts.find(p => p.id === selectedGroupProducts[group.id]);
                return (
                    <div key={group.id} style={{ marginBottom: '2em' }}>
                        <h3 style={{ marginBottom: '0.5em', borderBottom: '1px solid #e0e0e0', paddingBottom: '0.25em' }}>{group.name}</h3>
                        <table className='bundle-table' ref={includedTableRef}>
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }} />
                                    <th style={{ width: '110px' }} />
                                    <th>Product</th>
                                    <th style={{ width: '100px', textAlign: 'center' }}>Quantity</th>
                                    {canSeePricing && <th style={{ width: '100px', textAlign: 'right' }}>Price</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {groupProducts.map(product => {
                                    // A row is locked when the rep pre-configured this group (any
                                    // product in it is locked). The blanket disabled prop overrides everything.
                                    const rowLocked = disabled || groupLocked;
                                    // Clicking anywhere on the row selects the radio (same as clicking it).
                                    const selectRow = () => !rowLocked && setSelectedGroupProducts(prev => ({
                                        ...prev,
                                        [group.id]: product.id
                                    }));
                                    return (
                                    <tr
                                        key={product.id}
                                        onClick={selectRow}
                                        style={{ cursor: rowLocked ? undefined : 'pointer' }}
                                    >
                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                            <input
                                                type='radio'
                                                name={`group-${group.id}`}
                                                value={product.id}
                                                checked={selectedGroupProducts[group.id] === product.id}
                                                onChange={selectRow}
                                                // The row's onClick already selects; stop the click here so the
                                                // native input change and the row handler don't both fire.
                                                onClick={e => e.stopPropagation()}
                                                disabled={rowLocked}
                                                style={{ width: '18px', height: '18px', cursor: rowLocked ? 'not-allowed' : 'pointer' }}
                                            />
                                        </td>
                                        <td>
                                            <ProductImage
                                                src={product.image ?? undefined}
                                                fit='contain'
                                                style={{ width: '100px', height: '100px' }}
                                            />
                                        </td>
                                        <td>{product.name}</td>
                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }} onClick={e => e.stopPropagation()}>
                                            {renderItemQuantity(product)}
                                        </td>
                                        {canSeePricing && <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>{renderGroupItemPrice(product, selectedGroupProduct)}</td>}
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            })}

            {/* Non-grouped products */}
            {nonGroupedProducts.length > 0 && (
                <>
                    {hasGroups && <h3 style={{ marginBottom: '0.5em', borderBottom: '1px solid #e0e0e0', paddingBottom: '0.25em' }}>Additional Items</h3>}
                    <table className='bundle-table' ref={includedTableRef}>
                        <thead>
                            <tr>
                                <th style={{ width: '80px', textAlign: 'center' }}>Include</th>
                                <th style={{ width: '110px' }} />
                                <th>Product</th>
                                <th style={{ width: '100px', textAlign: 'center' }}>Quantity</th>
                                {canSeePricing && <th style={{ width: '100px', textAlign: 'right' }}>Price</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {nonGroupedProducts.map((product) => {
                                const isRequired = product.optionality !== 'Optional';
                                // A locked optional is shown checked+disabled (the rep pre-included it).
                                const optionalLocked = !isRequired && (disabled || isSelectionLocked(product.id));
                                // Whether this optional is currently included — mirrors the checkbox state
                                // (a locked optional counts as included). Drives the live price display.
                                const optionalSelected = optionalLocked ? true : (optionalSelections[product.id] ?? false);
                                // Only an unlocked optional row is interactive; required/locked rows do nothing on click.
                                const canToggle = product.optionality === 'Optional' && !optionalLocked;
                                // Clicking anywhere on the row toggles inclusion (same as the checkbox).
                                const toggleRow = () => canToggle && setOptionalSelections(prev => ({
                                    ...prev,
                                    [product.id]: !optionalSelected
                                }));
                                return (
                                    <tr
                                        key={product.id}
                                        onClick={toggleRow}
                                        style={{ ...(isRequired ? undefined : { fontStyle: 'italic' }), cursor: canToggle ? 'pointer' : undefined }}
                                    >
                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                            {product.optionality === 'Optional' ? (
                                                <Checkbox
                                                    checked={optionalSelected}
                                                    disabled={optionalLocked}
                                                    onChange={(_, d) => !optionalLocked && setOptionalSelections(prev => ({
                                                        ...prev,
                                                        [product.id]: d.checked as boolean
                                                    }))}
                                                    // The row's onClick already toggles; stop the click here so the
                                                    // checkbox change and the row handler don't both fire.
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            ) : (
                                                <Checkbox checked disabled />
                                            )}
                                        </td>
                                        <td>
                                            <ProductImage
                                                src={product.image ?? undefined}
                                                fit='contain'
                                                style={{ width: '100px', height: '100px' }}
                                            />
                                        </td>
                                        <td>{product.name}</td>
                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }} onClick={e => e.stopPropagation()}>
                                            {renderItemQuantity(product)}
                                        </td>
                                        {canSeePricing && <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>{
                                            product.optionality === 'Optional'
                                                ? renderOptionalItemPrice(product, optionalSelected)
                                                : renderRequiredItemPrice(product)
                                        }</td>}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}
            {!hideBundleTotal && canSeePricing && (estimatedPrice !== null || isEstimating) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '0.75em 1em', borderTop: '2px solid #e0e0e0', fontWeight: 'bold', marginTop: '0.5em', fontSize: '1.05em' }}>
                    <span style={{ marginRight: '1.5em' }}>Bundle Total:</span>
                    {isEstimating
                        ? <Spinner size='tiny' />
                        : estimatedPrice !== null && <CurrencyFormat value={estimatedPrice} />}
                </div>
            )}
        </div>
    );
};

export default BundleOptionsTables;
